import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { getCountryFlagUrl } from 'utils/countryFlagUrl';
import { Tooltip } from 'react-tooltip';

export const CurrencySelector = ({
  activeCurrency,
  setActiveCurrency,
  selectedTab,
  currenciesData,
  currencyPairsData,
}) => {
  const currencies = currenciesData?.currencies || [];
  const currencyPairs = currencyPairsData?.currencyPairs || [];

  // Ensure there's always a selected currency or currency pair
  useEffect(() => {
    if (
      selectedTab === 'swap' &&
      currencyPairs.length > 0 &&
      !activeCurrency?.currency1FlagCode
    ) {
      setActiveCurrency(currencyPairs[0]);
    } else if (
      selectedTab !== 'swap' &&
      currencies.length > 0 &&
      !activeCurrency?.flagCode
    ) {
      setActiveCurrency(currencies[0]);
    }
  }, [
    selectedTab,
    currencyPairs,
    currencies,
    activeCurrency,
    setActiveCurrency,
  ]);

  const isActive = (currency) => {
    if (!activeCurrency) return false;
    return currency.id === activeCurrency.id;
  };

  const renderSingleFlag = (currency) => {
    const active = isActive(currency);
    return (
      <motion.div
        key={currency.id}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.96 }}
        onClick={() => setActiveCurrency(currency)}
        className={`relative cursor-pointer transition-all duration-300 ${
          active
            ? 'bg-white/20 backdrop-blur-md border border-[#01B9FF] shadow-md'
            : 'border border-white/10'
        } flex items-center p-2 rounded-full`}
        data-tooltip-id={`currency-tooltip-${currency.id}`}
        data-tooltip-content={currency.name}
      >
        <img
          src={getCountryFlagUrl(currency.flagCode)}
          alt={currency.name}
          className="w-6 h-6 rounded-full border border-white/30"
        />
        {active && (
          <div className="ml-2 text-white/90 text-sm">{currency.name}</div>
        )}
        <Tooltip id={`currency-tooltip-${currency.id}`} place="top" />
      </motion.div>
    );
  };

  const renderPairFlags = (pair) => {
    const active = isActive(pair);
    return (
      <motion.div
        key={pair.id}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.96 }}
        onClick={() => setActiveCurrency(pair)}
        className={`relative flex items-center gap-2 cursor-pointer transition-all duration-300 px-2 py-1 rounded-full ${
          active
            ? 'bg-white/20 border border-[#01B9FF] backdrop-blur-md shadow-md'
            : 'border border-white/10'
        }`}
        data-tooltip-id={`pair-tooltip-${pair.id}`}
        data-tooltip-content={pair.name}
      >
        <div className="relative w-10 h-6">
          <img
            src={getCountryFlagUrl(pair.currency1FlagCode)}
            alt={pair.currency1Name}
            className="absolute w-5 h-5 border border-white/30 rounded-full z-10"
          />
          <img
            src={getCountryFlagUrl(pair.currency2FlagCode)}
            alt={pair.currency2Name}
            className="absolute w-5 h-5 left-3 border border-white/30 rounded-full z-20"
          />
        </div>
        {active && (
          <div className="text-xs text-white bg-[#01B9FF]/30 px-2 py-0.5 rounded-full backdrop-blur shadow-sm">
            {pair.currency1CurrencyCode} / {pair.currency2CurrencyCode}
          </div>
        )}
        <Tooltip id={`pair-tooltip-${pair.id}`} place="top" />
      </motion.div>
    );
  };

  return (
    <div className="flex items-center flex-wrap gap-1">
      {selectedTab === 'swap'
        ? currencyPairs.map(renderPairFlags)
        : currencies.map(renderSingleFlag)}
    </div>
  );
};
